import { useState, type FormEvent } from 'react';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@moneyskyline/ui';
import { AmountText } from '@/components/AmountText';
import {
  DraftExpiredError,
  UnresolvedLabelError,
  useCommitRuleDraft,
  useCreateRuleDraft,
  useDiscardRuleDraft,
  type RuleDraft,
} from '@/api/hooks/useRuleDraft';

export function NewConditionDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createDraft = useCreateRuleDraft();
  const commitDraft = useCommitRuleDraft();
  const discardDraft = useDiscardRuleDraft();
  const [sentence, setSentence] = useState('');
  const [draft, setDraft] = useState<RuleDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setSentence('');
    setDraft(null);
    setError(null);
    createDraft.reset();
    commitDraft.reset();
  };

  const preview = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const next = await createDraft.mutateAsync({ sentence });
      setDraft(next);
    } catch (caught) {
      if (caught instanceof UnresolvedLabelError) {
        setError(`Could not resolve “${caught.phrase}”. Rephrase and preview again.`);
        return;
      }
      if (caught instanceof DraftExpiredError) {
        setError('That preview expired. Preview again with the same sentence.');
        return;
      }
      setError('The preview could not be created. Nothing was applied.');
    }
  };

  const commit = async () => {
    if (!draft) return;
    setError(null);
    try {
      await commitDraft.mutateAsync({ draftId: draft.draft_id, hard: false });
      reset();
      onOpenChange(false);
    } catch (caught) {
      if (caught instanceof DraftExpiredError) {
        setError('That preview expired. Preview again before applying.');
        return;
      }
      setError('The condition could not be saved. Nothing was applied.');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && draft) discardDraft.mutate(draft.draft_id);
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg" data-testid="new-condition-dialog">
        <DialogHeader>
          <DialogTitle>New condition</DialogTitle>
        </DialogHeader>
        <form onSubmit={preview} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-muted-foreground">Tell me a condition</span>
            <textarea
              value={sentence}
              onChange={(event) => setSentence(event.target.value)}
              rows={3}
              className="min-h-11 rounded-md border border-input px-3 py-2 text-sm"
              placeholder='e.g. “card 0034 is business from March”'
            />
          </label>
          {error && <p role="alert" className="text-sm text-money-negative">{error}</p>}
          {draft && (
            <div className="rounded-[10px] border border-border bg-muted/30 p-3 text-sm" data-testid="condition-preview">
              <div className="font-semibold">{draft.restatement}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                Examples currently matching · {draft.preview_count}
              </div>
              {draft.preview_count === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Saving keeps the rule for future items; nothing in history changes.
                </p>
              )}
              {draft.samples.slice(0, 5).map((sample) => (
                <div key={sample.line_item_id} className="mt-1 flex min-h-11 items-center justify-between text-sm">
                  <span className="text-muted-foreground">{sample.transaction_id.slice(0, 8)}</span>
                  <AmountText value={sample.amount} />
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="submit" variant="outline" disabled={!sentence.trim() || createDraft.isPending}>
              {createDraft.isPending ? 'Previewing…' : 'Preview count'}
            </Button>
            <Button type="button" onClick={commit} disabled={!draft || commitDraft.isPending}>
              {commitDraft.isPending ? 'Saving…' : draft ? `Save & turn on · affects ${draft.preview_count}` : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default NewConditionDialog;
